import _ from 'lodash';
import dayjs from 'dayjs';
import { isEqual } from 'lodash';
import utc from 'dayjs/plugin/utc';
import { Box } from '@mui/material';
import { toastConfig } from 'config';
import { toast } from 'react-toastify';
import { dispatch } from 'store/store';
import debounce from 'lodash/debounce';
import { useSelector } from 'react-redux';
import { App, Form, Spin } from 'antd';
import { AutoIcon } from 'assets/icons/common';
import { useLocation, useNavigate } from 'react-router-dom';
import AntdValuePreview from 'components/dev/AntdValuePreview';
import { useGetQueryV3Mutation } from 'store/features/api/graphSlice';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { setMaxRecordedDate } from 'store/reducer/lease-management/leaseSlice';
import ComptrollerConfirmModal from 'pages/comptroller/ComptrollerConfirmModal';
import { useGetSingleIdentityMutation } from 'store/features/process/processApi';
import { useControlData } from '../../../lease-v9/lease-record-management-v9/Create';
import IdentityCaseActivityModal from 'pages/IdentityCase/IdentityCaseActivityModal';
import { constantFormValues } from '../../../lease-v9/lease-record-management-v9/root';
import LeaseRecordManagementV9ModifyHistory from './LeaseRecordManagementV9ModifyHistory';
import LeaseRecordManagementV9ModifySchedule from './LeaseRecordManagementV9ModifySchedule';
import LeaseRecordManagementV9ModifyBasicInfo from './LeaseRecordManagementV9ModifyBasicInfo';
import { isSequential, stateUpdater, utcConverter, isGrossRentLessThanAdvanceOnEachItem } from '../utils/helperFunc';
import { Button, Stack, Tab, Tabs, Tooltip, Typography } from '@mui/material';

const leaseDataCache = {};

dayjs.extend(utc);

const LeaseRecordManagementV9Modify = ({ screen = 'EDIT' }) => {
	const { message: messageApi } = App.useApp();
	const [form] = Form.useForm();
	const navigate = useNavigate();
	const { state } = useLocation();
	const actionButtonsRef = useRef();
	const leaseScheduleRef = useRef();
	const lastProcessedValues = useRef({});
	const leaseDetailsShowAllRef = useRef();

	const cacheKey = `${state?._id}-${state?.processCode}`;

	const initialCachedData = leaseDataCache[cacheKey];

	const [rowIndex, setRowIndex] = useState(0);
	const [selectedTab, setSelectedTab] = useState(0);
	const [changeRowAdded, setChangeRowAdded] = useState(false);
	const [previousFormData, setPreviousFormData] = useState({});
	const [originalChangesLength, setOriginalChangesLength] = useState(0);
	const pinStorageKey = `lease-record-management-v9-new-version-modify:pinned:${state?._id ?? 'unknown'}`;
	const [cachedLeaseData, setCachedLeaseData] = useState(initialCachedData);
	const [dataFullyLoaded, setDataFullyLoaded] = useState(!!initialCachedData);
	const [isHeaderPinned, setIsHeaderPinned] = useState(() => sessionStorage.getItem(pinStorageKey) !== '0');
	const [formState, setFormState] = useState({
		submitPayload: null,
		showFormSubmitModal: false,
		modalData: {
			head: state?.processName ?? '',
			label: 'Do you want to proceed?',
		},
	});
	const [originalValues, setOriginalValues] = useState({
		leaseTerm: null,
		grossRents: null,
		advanceAmounts: null,
		paymentModality: null,
		paymentFrequency: null,
		initialDiscountRate: null,
	});

	const { profileData } = useSelector((state) => state?.profile);

	const companyCode = profileData?.creatorOrganizationId;

	const { lynFormProperties } = useControlData();
	const [getLeaseTableData] = useGetQueryV3Mutation();
	const [getSingleLeaseData, { data: singleLeaseData, isLoading: isViewLoading }] = useGetSingleIdentityMutation();
	const [updateApi, { isLoading: isUpdateLoading, error: updateError, isSuccess: isUpdateSuccess, isError: isUpdateError }] =
		useGetSingleIdentityMutation();

	const displayLeaseData = cachedLeaseData || singleLeaseData;

	const statusData = useMemo(() => {
		return screen?.toLowerCase() === 'edit'
			? !(
					displayLeaseData?.activationStatus === 'W-I-P' &&
					displayLeaseData?.recordStatus === 'parked' &&
					profileData?.userId === displayLeaseData?.approverUserId[0]
				)
				? displayLeaseData?.data
				: displayLeaseData?.parkedData
			: [];
	}, [displayLeaseData]);

	dayjs.extend(utc);

	//simplified state setter
	const updateFormState = stateUpdater(setFormState);

	const setShowFormSubmitModal = (value) => {
		updateFormState('showFormSubmitModal', value);
	};

	// Fetch the data when component mounts
	useEffect(() => {
		// Reset tracking states on component mount
		setChangeRowAdded(false);
		lastProcessedValues.current = {};

		// If we don't have cached data or need to reset, show loading
		if (!initialCachedData) {
			setDataFullyLoaded(false);
		}

		// Function to fetch data
		const fetchLeaseData = async () => {
			try {
				const response = await getSingleLeaseData({
					suffixCode: '',
					_id: state?._id ?? '',
					operation: 'get-single',
					processCode: state?.processCode,
				}).unwrap();

				// Store in cache for future use
				if (!initialCachedData || !isEqual(initialCachedData, response)) {
					leaseDataCache[cacheKey] = response;
					setCachedLeaseData(response);
				}
			} catch (error) {
				console.error('Error fetching lease data:', error);
			}
		};

		if (initialCachedData) {
			// If we have cached data, fetch in background without showing loading
			requestAnimationFrame(() => {
				fetchLeaseData();
			});
		} else {
			// No cached data, fetch and show loading
			fetchLeaseData();
		}
	}, [initialCachedData, cacheKey, state?._id, state?.processCode]);

	// Handle API responses and navigation
	useEffect(() => {
		if (isUpdateSuccess) {
			toast.success('Success! Record updated successfully', toastConfig);
			navigate(-2);
		}
		if (isUpdateError) {
			if (updateError?.data?.code === '501') {
				toast.error(updateError?.data?.message, toastConfig);
			} else {
				toast.error('Failed! Action not completed', toastConfig);
			}
		}
	}, [isUpdateSuccess, updateError, isUpdateError]);

	// Set form values and initialize tracking when data loads
	useEffect(() => {
		if (!statusData || Object.keys(statusData).length === 0) return;

		const viewValues = {
			...statusData,

			tabIndex: '1',
			leaseId: statusData?.leaseId,
			leaseType: statusData?.leaseType,
			lessorName: statusData?.lessorName,
			leaseTitle: statusData?.leaseTitle,
			leaseTitle_new: statusData?.leaseTitle,
			paymentModality: statusData?.paymentModality,
			paymentFrequency: statusData?.paymentFrequency,
			paymentModality_new: statusData?.paymentModality,
			paymentFrequency_new: statusData?.paymentFrequency,
			lastModifiedDate: dayjs(statusData?.lastModifiedDate),
			modificationSequence: statusData?.modificationSequence,
			creationDate: dayjs(statusData?.creationDateTime?.localDateTimeStringAMPM) ?? null,

			leaseTerm: statusData?.leaseTerm,
			leaseTerm_new: statusData?.leaseTerm,
			grossRent: statusData?.grossRent,
			advanceAmount: statusData?.advanceAmount,
			advanceAmount_new: statusData?.advanceAmount,
			advanceDuration: statusData?.advanceDuration,
			securityDeposit: statusData?.securityDeposit,
			leaseEndDate: dayjs(statusData?.leaseEndDate),
			leaseEndDate_new: dayjs(statusData?.leaseEndDate),
			initialDirectCost: statusData?.initialDirectCost,
			securityDeposit_new: statusData?.securityDeposit,
			leaseStartDate: dayjs(statusData?.leaseStartDate),
			initialDirectCost_new: statusData?.initialDirectCost,
			upfrontLeasePayment: statusData?.upfrontLeasePayment,
			initialDiscountRate: statusData?.initialDiscountRate,
			upfrontLeasePayment_new: statusData?.upfrontLeasePayment,
			initialDiscountRate_new: statusData?.initialDiscountRate,
			endOfFinancialYear: dayjs(statusData?.endOfFinancialYear),
			escalationPercentage: statusData?.escalationPercentage || 0,

			month: statusData?.month,
			leaseName: statusData?.leaseName,
			grossRents: statusData?.grossRents,
			version: String(statusData?.version),
			grossRents_new: statusData?.grossRents,
			grossRentList: statusData?.grossRentList,
			agreementFiles: statusData?.agreementFiles,
			leaseLiability: statusData?.leaseLiability,
			advanceAmounts: statusData?.advanceAmounts,
			grossRentList_new: statusData?.grossRentList,
			advanceAmounts_new: statusData?.advanceAmounts,
			grossRentListSet: statusData?.grossRentListSet,
			advanceAmountList: statusData?.advanceAmountList,
			discountRateChanges: statusData?.discountRateChanges,
			cashIncentiveAmount: statusData?.cashIncentiveAmount,
			advanceAmountList_new: statusData?.advanceAmountList,
			advanceAmountListSet: statusData?.advanceAmountListSet,
			cashIncentiveAmount_new: statusData?.cashIncentiveAmount,

			changes: statusData?.changes?.map((item) => {
				const { inputChangeEffectiveDate, ...change } = item;

				return {
					...change,
					inputChangeEffectiveDate: dayjs(inputChangeEffectiveDate),
				};
			}),
		};

		// Store the original length of changes array
		setOriginalChangesLength(statusData?.changes?.length || 0);

		// Set row index based on existing changes
		setRowIndex(statusData?.changes?.length);

		// Set form values synchronously for immediate display
		form.setFieldsValue(viewValues);

		// For cached data, initialize immediately to avoid delay
		if (initialCachedData) {
			// Use requestAnimationFrame to ensure it happens after render
			requestAnimationFrame(() => {
				initializeFormTracking();
			});
		} else {
			// Wait for form to update before capturing original values
			// Use requestAnimationFrame instead of setTimeout for better timing
			requestAnimationFrame(() => {
				initializeFormTracking();
			});
		}
	}, [statusData, initialCachedData]);

	// Extracted function to initialize form tracking
	const initializeFormTracking = () => {
		// Store key field values for change detection
		const originalFieldValues = {
			leaseTerm: form.getFieldValue('leaseTerm_new'),
			initialDiscountRate: form.getFieldValue('initialDiscountRate_new'),
			paymentFrequency: form.getFieldValue('paymentFrequency_new'),
			paymentModality: form.getFieldValue('paymentModality_new'),
			grossRents: form.getFieldValue('grossRents_new'), // Don't stringify, keep as original object
			advanceAmounts: form.getFieldValue('advanceAmounts_new'), // Don't stringify, keep as original object
		};

		// Also store these in the lastProcessedValues to avoid duplicate processing
		lastProcessedValues.current = { ...originalFieldValues };

		// Store the captured values
		setOriginalValues(originalFieldValues);

		// Mark data as fully loaded AFTER setting original values

		setDataFullyLoaded(true);
	};

	// const [calculateData, setCalculateData] = useState({});
	const [selectHistory, setSelectHistory] = useState(null);

	const manageChangeRows = useCallback(() => {
		// Don't detect changes until data is fully loaded
		if (!dataFullyLoaded) {
			return;
		}

		const currentChangeList = form.getFieldValue('changes') || [];
		const maxAllowedRows = originalChangesLength + 1;

		// If we already have more than max allowed rows, trim to the max
		if (currentChangeList.length > maxAllowedRows) {
			const trimmedList = currentChangeList.slice(0, maxAllowedRows);
			form.setFieldsValue({ changes: trimmedList });
			setRowIndex(maxAllowedRows);
			// Update tracking state
			setChangeRowAdded(maxAllowedRows > originalChangesLength);
			return;
		}

		// Get current values from form
		const currentValues = {
			leaseTerm: form.getFieldValue('leaseTerm_new'),
			initialDiscountRate: form.getFieldValue('initialDiscountRate_new'),
			paymentFrequency: form.getFieldValue('paymentFrequency_new'),
			paymentModality: form.getFieldValue('paymentModality_new'),
			grossRents: form.getFieldValue('grossRents_new'),
			advanceAmounts: form.getFieldValue('advanceAmounts_new'),
		};

		// Check if we've already processed these exact values to prevent duplicate processing
		if (isEqual(currentValues, lastProcessedValues.current)) {
			return;
		}

		// Update last processed values
		lastProcessedValues.current = { ...currentValues };

		// Deep compare values to detect changes
		// const hasTermChanged = currentValues.leaseTerm !== originalValues.leaseTerm;
		const hasDiscountRateChanged = currentValues.initialDiscountRate !== originalValues.initialDiscountRate;
		const hasPaymentFrequencyChanged = currentValues.paymentFrequency !== originalValues.paymentFrequency;
		const hasPaymentModalityChanged = currentValues.paymentModality !== originalValues.paymentModality;

		// Use lodash isEqual for deep comparison of arrays
		const normalizeArray = (arr) => {
			if (!Array.isArray(arr)) return arr;
			return [...arr].map((v) => (typeof v === 'string' ? Number(v) || 0 : v || 0)).sort((a, b) => a - b);
		};

		const hasGrossRentsChanged = !isEqual(normalizeArray(currentValues.grossRents), normalizeArray(originalValues.grossRents));
		// const hasAdvanceAmountsChanged = !isEqual(normalizeArray(currentValues.advanceAmounts), normalizeArray(originalValues.advanceAmounts));

		// Track if any fields changed
		const hasChanges = hasDiscountRateChanged || hasGrossRentsChanged || hasPaymentFrequencyChanged || hasPaymentModalityChanged;

		// const hasChanges =
		// 	hasTermChanged || hasDiscountRateChanged || hasPaymentFrequencyChanged || hasPaymentModalityChanged || hasGrossRentsChanged;

		// Check if we should remove the added row when all changes are reverted
		if (!hasChanges && changeRowAdded && currentChangeList.length > originalChangesLength) {
			const updatedList = currentChangeList.slice(0, originalChangesLength);
			form.setFieldsValue({ changes: updatedList });
			setRowIndex(originalChangesLength);
			setChangeRowAdded(false);
			messageApi.info('Changes reverted. The added change record has been removed.');
			return;
		}

		// If no changes detected, exit early
		if (!hasChanges) {
			return;
		}

		// Check if we need to handle an existing row vs create new one
		const hasAutoadedRow = currentChangeList.length > originalChangesLength;
		let shouldCreateNewRow = !hasAutoadedRow;

		if (hasAutoadedRow) {
			const lastRow = currentChangeList[currentChangeList.length - 1];
			// If the last row has an effective date set, don't create a new row
			if (lastRow && lastRow.inputChangeEffectiveDate) {
				shouldCreateNewRow = false;
			}
		}

		// Determine the appropriate change description
		// let changeDescription = 'Initial Rent';

		// if (hasGrossRentsChanged) {
		// 	changeDescription = 'Change In rent';
		// } else if (hasDiscountRateChanged) {
		// 	changeDescription = 'Change In IBR';
		// } else if (hasPaymentFrequencyChanged) {
		// 	changeDescription = 'Change In Payment Frequency';
		// } else if (hasPaymentModalityChanged) {
		// 	changeDescription = 'Change In Payment Modality';
		// }

		const changes = [
			hasGrossRentsChanged && 'Change In Rent',
			hasDiscountRateChanged && 'Change In IBR',
			hasPaymentFrequencyChanged && 'Change In Payment Frequency',
			hasPaymentModalityChanged && 'Change In Payment Modality',
		].filter(Boolean);

		const changeDescription = changes.length ? changes.join(', ') : 'Initial Rent';

		// Create the row data
		const newRow = {
			paymentFrequency: currentValues.paymentFrequency,
			paymentTime: currentValues.paymentModality,
			inputChangeLeaseTerm: currentValues.leaseTerm,
			inputChangeGrossAmount: 0,
			inputChangeDiscountRate: currentValues.initialDiscountRate,
			inputChangeEscalation: form.getFieldValue('escalationPercentage') || 0,
			inputChangeAdvance: form.getFieldValue('advanceAmount'),
			inputChangeAdvanceAmounts: currentValues.advanceAmounts,
			inputChangeGrossRents: currentValues.grossRents,
			inputChangeDescription: changeDescription,
		};

		if (shouldCreateNewRow) {
			// Only create a new row if we haven't already added one
			if (currentChangeList.length < maxAllowedRows) {
				newRow.inputChangeEffectiveDate = null;
				const updatedList = [...currentChangeList, newRow];
				form.setFieldsValue({ changes: updatedList });
				setRowIndex(updatedList.length);
				setChangeRowAdded(true);

				// Mark the effective date field as required
				requestAnimationFrame(() => {
					const newRowIndex = updatedList.length - 1;
					form.setFields([
						{
							name: ['changes', newRowIndex, 'inputChangeEffectiveDate'],
							errors: ['Effective Date is required'],
							touched: true,
						},
					]);
				});

				messageApi.info('A new change record has been added. Please set the effective date.');
			}
		} else {
			// Update the existing added row
			const lastRowIndex = currentChangeList.length - 1;
			const lastRow = currentChangeList[lastRowIndex];

			// Preserve the effective date and other fields
			if (lastRow) {
				newRow.inputChangeEffectiveDate = lastRow.inputChangeEffectiveDate;
				if (lastRow.inputChangeMonth) {
					newRow.inputChangeMonth = lastRow.inputChangeMonth;
				}
			}

			const updatedList = [...currentChangeList];
			updatedList[lastRowIndex] = newRow;
			form.setFieldsValue({ changes: updatedList });

			// Notify if description changed
			if (lastRow && lastRow.inputChangeDescription !== changeDescription) {
				messageApi.info(`Change type updated to "${changeDescription}"`);
			}
		}
	}, [form, originalValues, changeRowAdded, dataFullyLoaded, originalChangesLength, lastProcessedValues]);
	// Use a debounced version of the change handler to avoid rapid consecutive calls
	const debouncedManageChangeRows = useMemo(() => debounce(manageChangeRows, 300), [manageChangeRows]);

	// Expose debouncedManageChangeRows to the window object so child components can access it
	useEffect(() => {
		if (screen === 'EDIT') {
			window.debouncedManageChangeRows = debouncedManageChangeRows;
		}

		return () => {
			// Clean up when component unmounts
			if (window.debouncedManageChangeRows === debouncedManageChangeRows) {
				delete window.debouncedManageChangeRows;
			}
		};
	}, [debouncedManageChangeRows, screen]);

	const handleSubmit = async () => {
		try {
			await form.validateFields();

			const formData = form.getFieldsValue(true);

			if (formData?.paymentFrequency_new === 'Quarterly' && formData?.leaseTerm_new % 3 !== 0) {
				messageApi.error(
					"Total lease months must be a multiple of 3 for quarterly payments. Please correct the Lease Payment Schedule's To Month field"
				);
				return;
			}
			if (!isSequential(formData?.grossRentList_new)) {
				messageApi.error('Gross Rent List must be sequential');
				return;
			}
			if (!isSequential(formData?.advanceAmountList_new)) {
				messageApi.error('Advance Amount List must be sequential');
				return;
			}

			let grossRentListSet = formData?.grossRentListSet || [];
			let advanceAmountListSet = formData?.advanceAmountListSet || [];
			let effectiveDateHistory = formData?.effectiveDateHistory || [];
			grossRentListSet?.push(formData?.grossRentList);

			effectiveDateHistory?.push(utcConverter(formData?.changes?.[formData?.changes?.length - 1]?.inputChangeEffectiveDate));

			const changesPayload = formData.changes?.map((change) => {
				if (change.inputChangeEffectiveDate) {
					return {
						...change,
						inputChangeAdvance: change.inputChangeAdvanceAmounts?.reduce((accumulator, currentValue) => accumulator + currentValue, 0),
						inputChangeEffectiveDate: utcConverter(change.inputChangeEffectiveDate),
						upfrontLeasePayment: formData?.upfrontLeasePayment_new,
						initialDirectCost: formData?.initialDirectCost_new,
						securityDeposit: formData?.securityDeposit_new,
						cashIncentiveAmount: formData?.cashIncentiveAmount_new ?? 0,
					};
				}
				return change;
			});
			const isGrossRentAndAdvanceAmountValid = isGrossRentLessThanAdvanceOnEachItem(formData?.grossRents_new, formData?.advanceAmounts_new);

			if (!isGrossRentAndAdvanceAmountValid) {
				messageApi.error('Inconsistency between Lease Payment and Advance Adjustment');
				return;
			}

			const editPagePayload = {
				isRevised: false,
				effectiveDateHistory,
				advanceAmountListSet,
				changes: changesPayload,
				lessorID: formData?.lessorID,
				leaseType: formData?.leaseType,
				leaseTerm: formData?.leaseTerm_new,
				lessorName: formData?.lessorName,
				editCount: statusData?.editCount,
				grossRents: formData?.grossRents_new,
				leaseTitle: formData?.leaseTitle_new,
				grossRentListSet: grossRentListSet,
				leaseEndDate: formData?.leaseEndDate_new,
				advanceAmount: formData?.advanceAmount_new,
				grossRentList: formData?.grossRentList_new,
				lastModifiedDate: utcConverter(dayjs()),
				modifiedDateTime: utcConverter(dayjs()),
				modifyCount: statusData?.modifyCount + 1,
				advanceAmounts: formData?.advanceAmounts_new,
				leaseStartDate: formData?.leaseStartDate,
				paymentModality: formData?.paymentModality_new,
				securityDeposit: formData?.securityDeposit_new,
				paymentFrequency: formData?.paymentFrequency_new,
				initialDirectCost: formData?.initialDirectCost_new,
				advanceAmountList: formData?.advanceAmountList,
				initialDiscountRate: formData?.initialDiscountRate_new,
				upfrontLeasePayment: formData?.upfrontLeasePayment_new,
				cashIncentiveAmount: formData?.cashIncentiveAmount_new,
				modificationSequence: statusData?.modificationSequence + 1,
				advanceDuration: formData?.advanceAmounts_new?.filter((item) => item !== 0).length,
				paymentModalityLabel: constantFormValues?.paymentModality?.options?.find((item) => item.value === formData?.paymentModality_new)
					?.label,
			};

			updateFormState('showFormSubmitModal', true);
			updateFormState('modalData.head', 'Confirmation');
			updateFormState('submitPayload', editPagePayload);
			updateFormState('modalData.label', 'Do you want to update this lease record?');
		} catch (error) {
			console.error('Validation Failed:', error);
			toast.error('Please fill all required fields', toastConfig);
		}
	};

	const handleModalSubmit = () => {
		updateApi({
			operation: 'update-single',
			processCode: state?.processCode,
			suffixCode: '',
			data: formState?.submitPayload,
			_id: state._id,
			version: +singleLeaseData?.version,
		});
	};

	const hasFormDataChanged = () => {
		const currentFormData = form.getFieldsValue(true);

		if (screen?.toLowerCase() !== 'create') {
			const currentPayload = {
				advanceAmount: currentFormData?.changes?.[0]?.inputChangeAdvanceAmounts?.reduce((acc, curr) => acc + curr, 0),
				leaseTerm: currentFormData?.changes?.[0]?.inputChangeLeaseTerm,
				discountRate: currentFormData?.changes?.[0]?.inputChangeDiscountRate,
				startDay: String(currentFormData?.leaseStartDate?.date()),
				startMonth: String(currentFormData?.leaseStartDate?.month() + 1),
				startYear: String(currentFormData?.leaseStartDate?.year()),
				paymentFrequency: currentFormData?.changes?.[0]?.paymentFrequency,
				paymentTime: currentFormData?.changes?.[0]?.paymentTime,
				advanceDuration: currentFormData?.changes?.[0]?.inputChangeAdvanceAmounts?.filter((item) => item !== 0)?.length,
				upfrontLeasePayment: currentFormData?.changes?.[0]?.upfrontLeasePayment,
				initialDirectCost: currentFormData?.changes?.[0]?.initialDirectCost,
				securityDeposit: currentFormData?.securityDeposit,
				endOfFinancialYear: currentFormData?.endOfFinancialYear,
				advanceAmounts: currentFormData?.changes?.[0]?.inputChangeAdvanceAmounts,
				grossRents: currentFormData?.changes?.[0]?.inputChangeGrossRents,
			};

			const prevPayload = {
				...previousFormData,
			};

			// Compare payloads as JSON strings
			const currentStr = JSON.stringify(currentPayload);
			const prevStr = JSON.stringify(prevPayload);

			if (currentStr !== prevStr) {
				setPreviousFormData(currentPayload);
				return true;
			}
			return false;
		}

		// For CREATE mode
		const currentPayload = {
			advanceAmount: currentFormData?.advanceAmount,
			leaseTerm: currentFormData?.leaseTerm,
			discountRate: currentFormData?.initialDiscountRate,
			startDay: String(currentFormData?.leaseStartDate?.date()),
			startMonth: String(currentFormData?.leaseStartDate?.month() + 1),
			startYear: String(currentFormData?.leaseStartDate?.year()),
			paymentFrequency: currentFormData?.paymentFrequency,
			paymentTime: currentFormData?.paymentModality,
			advanceDuration: currentFormData?.advanceAmounts?.filter((item) => item !== 0)?.length,
			upfrontLeasePayment: currentFormData?.upfrontLeasePayment,
			initialDirectCost: currentFormData?.initialDirectCost,
			securityDeposit: currentFormData?.securityDeposit,
			endOfFinancialYear: currentFormData?.endOfFinancialYear,
			advanceAmounts: currentFormData?.advanceAmounts,
			grossRents: currentFormData?.grossRents,
		};

		const currentStr = JSON.stringify(currentPayload);
		const prevStr = JSON.stringify(previousFormData);

		if (currentStr !== prevStr) {
			setPreviousFormData(currentPayload);
			return true;
		}
		return false;
	};

	const selectTabHandler = (event, newValue) => {
		setSelectedTab(newValue);
		debouncedManageChangeRows();

		if (newValue === 1 && dataFullyLoaded) {
			if (leaseScheduleRef.current?.queryV3Loader) {
				leaseScheduleRef.current.queryV3Loader();
			}
		}

		if (newValue === 4 && dataFullyLoaded) {
			if (leaseDetailsShowAllRef.current?.loadData) {
				leaseDetailsShowAllRef.current.loadData();
			}
		}
	};

	useEffect(() => {
		if (dataFullyLoaded) {
			if (actionButtonsRef.current?.flag?.check !== 0) {
				if (hasFormDataChanged()) {
					actionButtonsRef?.current?.handleDataLoading();
				}
			} else {
				actionButtonsRef?.current?.queryV3Loader();
			}

			if (singleLeaseData?.data?.version) {
				const version = String(singleLeaseData?.data?.version);

				getLeaseTableData({
					companyCode,
					fieldCodes: ['companyCode', 'identityRecordIdString', 'version'],
					fieldValues: [companyCode, state._id, version],
					name: 'leaseSchedulesOnVersion-v9',
				}).then((res) => {
					const results = res?.data?.results || [];

					const relevantStatuses = ['Pending', 'Recorded', 'Reversed'];

					const relevantItems = _.filter(results, (item) => relevantStatuses.includes(item.processStatus));

					const latestItem = _.maxBy(relevantItems, 'date');

					const overallMaxDate = latestItem?.date;

					dispatch(
						setMaxRecordedDate({
							minimumDateLimit: overallMaxDate,
							leaseId: String(singleLeaseData?.data?.leaseId),
						})
					);
				});
			}
		}
	}, [dataFullyLoaded, screen, singleLeaseData]);

	const tabItem = [
		{
			key: '1',
			label: 'Lease Details',
			children: (
				<LeaseRecordManagementV9ModifyBasicInfo
					handleSubmit={handleSubmit}
					vendorList={lynFormProperties?.vendorList}
					initialChangeLength={originalChangesLength}
					debouncedManageChangeRows={debouncedManageChangeRows}
				/>
			),
		},
		{
			key: '2',
			label: 'Lease Schedule',
			children: (
				<>
					<LeaseRecordManagementV9ModifySchedule
						screen={screen}
						// ref={leaseScheduleRef}
						viewData={singleLeaseData}
						dataFullyLoaded={dataFullyLoaded}
						checkFormValidation={form.validateFields}
					/>
				</>
			),
		},
		{
			key: '3',
			label: 'History',
			children: (
				<>
					<LeaseRecordManagementV9ModifyHistory viewData={singleLeaseData} selectHistory={setSelectHistory} />
				</>
			),
		},
	];

	const showSpinner = isUpdateLoading || (isViewLoading && !initialCachedData);

	useEffect(() => {
		if (displayLeaseData && displayLeaseData.activationStatus !== undefined) {
			window.leaseActivationStatus = displayLeaseData.activationStatus;
		}
	}, [displayLeaseData]);

	useEffect(() => {
		setIsHeaderPinned(sessionStorage.getItem(pinStorageKey) !== '0');
	}, [pinStorageKey]);

	const handleAutoPinToggle = () => {
		const nextState = !isHeaderPinned;
		setIsHeaderPinned(nextState);
		sessionStorage.setItem(pinStorageKey, nextState ? '1' : '0');
	};

	return (
		<Box>
			<Spin spinning={showSpinner} style={{ maxHeight: '100%' }}>
				<Box
					sx={{
						mb: 2,
						backgroundColor: isHeaderPinned ? 'background.main' : 'transparent',
						top: isHeaderPinned ? '60px' : 'auto',
						zIndex: isHeaderPinned ? 20 : 'auto',
						position: isHeaderPinned ? 'sticky' : 'static',
						px: { xs: 2, md: 2.5 },
						pt: { xs: 1.75, md: 2 },
						bgcolor: '#fff',
						border: '1px solid #e6ebf2',
						borderRadius: '10px',
						boxShadow: 'none',
					}}
				>
					<Stack
						direction={{ xs: 'column', md: 'row' }}
						alignItems={{ xs: 'flex-start', md: 'center' }}
						justifyContent="space-between"
						spacing={2}
						sx={{ mb: 1.25 }}
					>
						<Box>
							<Typography sx={{ color: '#1f2937', fontSize: '18px', fontWeight: 600, lineHeight: 1.2 }}>
								Modify {state?.processName}
							</Typography>
						</Box>

						<Tooltip title={isHeaderPinned ? 'Pinned' : 'Unpin'}>
							<Button
								onClick={handleAutoPinToggle}
								aria-label={isHeaderPinned ? 'Unpin header' : 'Pin header'}
								sx={{
									minWidth: '36px',
									width: '36px',
									height: '36px',
									borderRadius: '10px',
									color: isHeaderPinned ? '#1D4ED8' : '#374151',
									border: `1px solid ${isHeaderPinned ? '#BFDBFE' : '#E5E7EB'}`,
									backgroundColor: isHeaderPinned ? '#EFF6FF' : '#FFFFFF',
								}}
							>
								<AutoIcon />
							</Button>
						</Tooltip>
					</Stack>

					<Box
						sx={{
							px: { xs: 0.25, md: 0.5 },
							borderTop: '1px solid #eef2f7',
						}}
					>
						<Tabs
							value={selectedTab}
							onChange={selectTabHandler}
							variant="scrollable"
							scrollButtons="auto"
							allowScrollButtonsMobile
							sx={{
								minHeight: '36px',
								'& .MuiTabs-indicator': {
									height: '2px',
									backgroundColor: '#2563eb',
									borderRadius: '999px',
								},
								'& .MuiTab-root': {
									minHeight: '36px',
									px: { xs: 1.25, md: 1.5 },
									mr: { xs: 0.5, md: 1 },
									textTransform: 'none',
									fontSize: '14px',
									fontWeight: 500,
									color: '#667085',
								},
								'& .Mui-selected': {
									color: '#2563eb !important',
									fontWeight: 600,
								},
							}}
						>
							{tabItem?.map((item) => (
								<Tab key={item.key} label={item.label} />
							))}
						</Tabs>
					</Box>
				</Box>

				<Form form={form} layout="vertical" autoComplete="off" requiredMark={false} onFinish={handleSubmit}>
					{/* <AntdValuePreview form={form} show={true} /> */}

					{tabItem?.map((item, index) => (
						<Box
							key={index}
							style={{
								display: selectedTab === index ? 'block' : 'none',
							}}
						>
							{item.children}
						</Box>
					))}
				</Form>

				{formState?.showFormSubmitModal && (
					<ComptrollerConfirmModal
						modalData={formState?.modalData}
						handleSubmit={handleModalSubmit}
						setShowModal={setShowFormSubmitModal}
						showModal={formState?.showFormSubmitModal}
					/>
				)}

				{selectHistory && (
					<IdentityCaseActivityModal
						title={state?.processName}
						selectItem={selectHistory}
						setSelectHistory={setSelectHistory}
						processCode={singleLeaseData?.processCode}
						processVersion={singleLeaseData?.parkProcessVersion}
					/>
				)}
			</Spin>
		</Box>
	);
};

export default LeaseRecordManagementV9Modify;